/**
 * Pre-flight record checks. Pure: no context, no network, no schema files.
 *
 * The IRS validates the WHOLE transmission, so one unfileable record rejects every record
 * beside it. core-api handled that by quarantining bad records before building the XML
 * (a2a-integration/recordValidation.ts). TabFile needs the same answer earlier and in
 * more detail: which record, which field, which rule, in words a customer can act on.
 *
 * This combines three sources:
 *  - recordValidation.validateFileableRecord: the state / ZIP / foreign-address rules, moved
 *    across unchanged and generalized to every form's column names (see fieldMap.ts);
 *  - the required fields each form's generator cannot build a return without;
 *  - the zod shape of the record (schemas.ts), for amounts that are not numbers.
 */
import type { CheckIssue, CheckResult, FormType } from "../context";
import { FORM_IDENTITY, pick, pickField } from "./fieldMap";
import { canonicalCheckbox, recordSchema } from "./schemas";
import { fieldRules } from "./fieldRules";
import { irsCountryCode } from "./countryCodes.generated";
import { FIELD_CHECK_CODES, calendarDayOf, checkFieldValues } from "./fieldChecks";
import { SUPPORTED_SCHEMA_YEARS } from "../schema/xsdValidation";

export const CHECK_CODES = {
  MISSING_FIELD: "MISSING_FIELD",
  INVALID_STATE: "INVALID_STATE",
  INVALID_ZIP: "INVALID_ZIP",
  UNKNOWN_COUNTRY: "UNKNOWN_COUNTRY",
  INVALID_TIN: "INVALID_TIN",
  INVALID_AMOUNT: "INVALID_AMOUNT",
  UNSUPPORTED_TAX_YEAR: "UNSUPPORTED_TAX_YEAR",
  TAX_YEAR_MISMATCH: "TAX_YEAR_MISMATCH",
  WITHHOLDING_EXCEEDS_INCOME: "WITHHOLDING_EXCEEDS_INCOME",
  DATE_OUTSIDE_TAX_YEAR: "DATE_OUTSIDE_TAX_YEAR",
  DUPLICATE_RECORD_REF: "DUPLICATE_RECORD_REF",
  CONFLICTING_BOXES: "CONFLICTING_BOXES",
  INVALID_CODE: "INVALID_CODE",
  INVALID_POSTAL_CODE: FIELD_CHECK_CODES.INVALID_POSTAL_CODE,
  INVALID_DATE: FIELD_CHECK_CODES.INVALID_DATE,
  INVALID_PHONE: FIELD_CHECK_CODES.INVALID_PHONE,
  INVALID_EMAIL: FIELD_CHECK_CODES.INVALID_EMAIL,
  INVALID_TEXT: FIELD_CHECK_CODES.INVALID_TEXT,
  INVALID_ID_TYPE: FIELD_CHECK_CODES.INVALID_ID_TYPE,
} as const;

const toAmount = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(String(v ?? "").replace(/[,$\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Federal withholding against the income it was withheld from. The IRS rule behind this
 * (F1099NEC001_001 and its siblings) flags withholding that "appears excessive", and the IRS
 * test system rejected a whole 1,000-record transmission over a single record whose
 * withholding equalled its income. Backup withholding is 24 percent, so withholding above a
 * quarter of the income on the return is refused before anything is sent.
 */
const WITHHOLDING_LIMIT = 0.24;
function withholdingExceedsIncome(r: Record<string, unknown>): { withheld: number; income: number } | null {
  const withheldKey = Object.keys(r).find((k) => /^BOX\d*[A-Z]*_?FED(ERAL)?_INCOME_TAX_WITHHELD$/i.test(k));
  if (!withheldKey) return null;
  const withheld = toAmount(r[withheldKey]);
  if (withheld <= 0) return null;
  let income = 0;
  for (const [k, v] of Object.entries(r)) {
    if (k === withheldKey) continue;
    if (!/^BOX\d+[A-Z]?_/i.test(k)) continue;
    // Codes are not money: 1099-R Box 7 is BOX7_DISTRIBUTION_CODES, and its "7" was added to
    // the income.
    if (/WITHHELD|_IND$|INDICATOR|STATE|LOCAL|_CODES?$|_CD$|NUM|NUMBER|_ID$|DATE|_DESC/i.test(k)) continue;
    income += Math.max(0, toAmount(v));
  }
  return withheld > income * WITHHOLDING_LIMIT + 0.005 ? { withheld, income } : null;
}

export function checkRecords(
  formType: FormType | string,
  taxYear: number,
  records: Record<string, unknown>[]
): CheckResult {
  const map = FORM_IDENTITY[String(formType)] ?? FORM_IDENTITY["1099-NEC"];
  const schema = recordSchema(formType as FormType, taxYear);
  const rules = fieldRules(String(formType), taxYear);
  const issues: CheckIssue[] = [];
  const blockedRows = new Set<number>();

  const oldestSchemaYear = Math.min(...SUPPORTED_SCHEMA_YEARS);
  const list = Array.isArray(records) ? records : [];

  const add = (
    index: number,
    record: Record<string, unknown>,
    code: string,
    message: string,
    field?: string,
    severity: "error" | "warning" = "error",
    rule?: string
  ) => {
    issues.push({
      index,
      recordRef: record?.recordRef !== undefined ? String(record.recordRef) : undefined,
      field,
      code,
      ...(rule ? { rule } : {}),
      message,
      severity,
    });
    if (severity === "error") blockedRows.add(index);
  };

  /*
   * recordRef is the customer's stable name for one return, and everything downstream is
   * keyed by it: the lineage that records what the IRS accepted, the correction that names
   * the accepted record it amends, the replacement that re-sends a rejected one. Two rows
   * carrying the same recordRef break all three.
   *
   * It matters most in a correction. Each correction return names the prior IRS record it
   * corrects; five rows sharing one recordRef would be five corrections of a single accepted
   * return in one transmission, with no defined order between them, and the recipient would
   * end up with whichever landed last. The IRS way is one correction per accepted return -
   * fix every box of that return in the one record, and correct it again only after the
   * first correction is acknowledged.
   *
   * The two HTTP entry points that take records as JSON have refused this for a long time.
   * An uploaded spreadsheet did not go through them, so the same file was accepted whole.
   */
  const refRows = new Map<string, number[]>();
  list.forEach((record, index) => {
    const ref = (record as Record<string, unknown>)?.recordRef;
    if (ref === undefined || ref === null || String(ref).trim() === "") return;
    const rows = refRows.get(String(ref));
    if (rows) rows.push(index);
    else refRows.set(String(ref), [index]);
  });

  // 1099-Q: how many rows each recipient TIN has, for the account number rule below.
  const q1099Tins = new Map<string, number>();
  if (String(formType) === "1099-Q") {
    for (const record of list) {
      const tin = String((record as Record<string, unknown>)?.RECIPIENT_ID_NUMBER ?? "").replace(/\D/g, "");
      if (tin) q1099Tins.set(tin, (q1099Tins.get(tin) ?? 0) + 1);
    }
  }

  list.forEach((record, index) => {
    const r = (record || {}) as Record<string, unknown>;

    // ---- 0. one row per recordRef ----
    const ref = r.recordRef === undefined ? "" : String(r.recordRef);
    const sharing = ref ? (refRows.get(ref) ?? []) : [];
    if (sharing.length > 1) {
      const others = sharing.filter((row) => row !== index).map((row) => row + 1);
      add(
        index,
        r,
        CHECK_CODES.DUPLICATE_RECORD_REF,
        `"${ref}" is also the recordRef of ${others.length === 1 ? "row" : "rows"} ` +
          `${others.slice(0, 5).join(", ")}${others.length > 5 ? ", and others" : ""}. ` +
          "One row per recordRef: it is how a return is matched to the one it corrects or " +
          "replaces. Give each return its own recordRef, or put every fix for this return in " +
          "a single row.",
        "recordRef"
      );
    }

    // ---- 1. shape: amounts that are not numbers, and missing required columns ----
    const parsed = (schema as any).safeParse(r);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path.map(String).join(".") || undefined;
        const isAmount = /must be a number/i.test(issue.message);
        // core-api gives every column a label; use it, so a customer reads "Total Ordinary
        // Dividends" rather than BOX1A_TOTAL_ORDINARY_DIVIDENDS.
        const label = (field && rules[field]?.label) || field;
        add(
          index,
          r,
          isAmount ? CHECK_CODES.INVALID_AMOUNT : CHECK_CODES.MISSING_FIELD,
          isAmount
            ? `${label} must be an amount, but this row has "${String(r[field as string] ?? "")}".`
            : `${issue.message}${label ? ` (${label})` : ""}.`,
          field
        );
      }
    }

    // ---- 2. required identity fields ----
    // Whether each TIN is a valid nine digits is step 4's job, along with every other column.
    const filerTin = pick(r, map.filerTin);
    if (!filerTin) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        "The filer's federal identification number is missing. The IRS cannot accept a return without it.",
        map.filerTin[0]
      );
    }

    const recipientTin = pick(r, map.recipientTin);
    if (!recipientTin) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        "The recipient's taxpayer identification number is missing.",
        map.recipientTin[0]
      );
    }

    const hasName = !!pick(r, map.personName) || !!pick(r, map.businessName);
    if (!hasName) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        "The recipient has no name. Supply either a person name or a business name.",
        map.personName[0]
      );
    } else if (map.personName.includes("RECIPIENT_FIRST_NAME")) {
      // A last name with no first name, and nothing else to name the recipient by. The IRS
      // PersonName needs a first name before the last, so the generators either filed an
      // empty first name, which the schema refuses at send time ("PersonLastNm: This element
      // is not expected"), or a made-up "UNKNOWN". The usual cause is a business written into
      // RECIPIENT_LAST_NAME. Which one it is cannot be told from the row, so it is refused
      // here, at the check, rather than guessed.
      const filledText = (field: string) => String(r[field] ?? "").trim() !== "";
      const lastOnly =
        filledText("RECIPIENT_LAST_NAME") &&
        !filledText("RECIPIENT_FIRST_NAME") &&
        !["RECIPIENT_NAME", "RECIPIENT_NAME1"].some(filledText) &&
        !pick(r, map.businessName);
      if (lastOnly) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          `The recipient has a last name (${String(r.RECIPIENT_LAST_NAME).trim()}) but no first name. ` +
            "The IRS needs a first name for a person. If this recipient is a business, put the " +
            "name in RECIPIENT_BUSINESS_NAME1 and set RECIPIENT_ID_TYPE to 1 (EIN).",
          "RECIPIENT_FIRST_NAME",
          "error",
          "PERSON_FIRST_NAME"
        );
      }
    }

    // ---- 2b. withholding that cannot be right ----
    const excess = withholdingExceedsIncome(r);
    if (excess) {
      const withheldField = Object.keys(r).find((k) => /FED(ERAL)?_INCOME_TAX_WITHHELD$/i.test(k));
      add(
        index,
        r,
        CHECK_CODES.WITHHOLDING_EXCEEDS_INCOME,
        `Federal income tax withheld (${excess.withheld.toFixed(2)}) is more than 24% of the income reported (${excess.income.toFixed(2)}). ` +
          "The IRS treats that as excessive and rejects the whole transmission it is in.",
        withheldField
      );
    }

    // ---- 2c. 1099-B dates against the tax year ----
    // IRIS form rules F1099B023-025 (new in the 2026-09-24 TY2025 and TY2026 rules), each a
    // reject: the date acquired must not be after the end of the tax year, the date sold must
    // fall within it, and the date sold must not come before the date acquired. A date that is
    // not a real date at all is step 4's to report.
    if (String(formType) === "1099-B" && Number.isFinite(taxYear)) {
      const acquired = calendarDayOf(String(r.BOX_1B_DATE_ACQUIRED ?? ""));
      const sold = calendarDayOf(String(r.BOX_1C_DATE_SOLD ?? ""));
      if (acquired && acquired > `${taxYear}-12-31`) {
        add(
          index,
          r,
          CHECK_CODES.DATE_OUTSIDE_TAX_YEAR,
          `The date acquired (${String(r.BOX_1B_DATE_ACQUIRED)}) is after the end of tax year ${taxYear}. ` +
            "The IRS rejects a 1099-B acquired after the year it reports.",
          "BOX_1B_DATE_ACQUIRED"
        );
      }
      if (sold && (sold < `${taxYear}-01-01` || sold > `${taxYear}-12-31`)) {
        add(
          index,
          r,
          CHECK_CODES.DATE_OUTSIDE_TAX_YEAR,
          `The date sold or disposed (${String(r.BOX_1C_DATE_SOLD)}) is not in tax year ${taxYear}. ` +
            "A 1099-B reports sales made during the tax year.",
          "BOX_1C_DATE_SOLD"
        );
      }
      if (acquired && sold && sold < acquired) {
        add(
          index,
          r,
          CHECK_CODES.DATE_OUTSIDE_TAX_YEAR,
          `The date sold or disposed (${String(r.BOX_1C_DATE_SOLD)}) is before the date acquired ` +
            `(${String(r.BOX_1B_DATE_ACQUIRED)}).`,
          "BOX_1C_DATE_SOLD"
        );
      }
    }

    // ---- 2d. 1099-Q boxes the XSD or the form constrains ----
    // Boxes 1 and 3 are USDecimalAmountNNType, so a negative gross distribution or basis fails
    // the schema and with it the whole transmission. Box 2 (earnings) may be a loss.
    // The rest follows the Instructions for Form 1099-Q (Rev. 04/2025):
    //  - box 5: "Check box 5a ... or check box 5b ... Otherwise, check box 5c": exactly one
    //    (IRIS rule F1099Q002). The XSD would take none, or all three, ticked;
    //  - boxes 1, 3 and 4: IRIS rules F1099Q001, F1099Q003 and F1099Q008_001, below;
    //  - account number: "required if you have multiple accounts for a recipient for whom you
    //    are filing more than one Form 1099-Q";
    //  - distribution code (box 7 in the instructions): optional, one of 1 to 6.
    if (String(formType) === "1099-Q") {
      for (const [field, box] of [
        ["BOX1_GROSS_DISTRIBUTION", "Box 1 (gross distribution)"],
        ["BOX3_BASIS", "Box 3 (basis)"],
      ] as const) {
        const raw = r[field];
        if (raw === undefined || raw === null || String(raw).trim() === "") continue;
        if (toAmount(raw) < 0) {
          add(
            index,
            r,
            CHECK_CODES.INVALID_AMOUNT,
            `${box} is ${String(raw).trim()}. The IRS does not accept a negative amount here; ` +
              "only box 2 (earnings) can be negative.",
            field,
            "error",
            "NEGATIVE_AMOUNT"
          );
        }
      }

      // A box 2 loss: "Enter a (loss) in box 2 only if this is the final year for distributions
      // from the account" (Instructions for Form 1099-Q). Nothing in a row says which year that
      // is and no IRIS rule checks it, so the filer is asked to confirm rather than refused.
      if (toAmount(r.BOX2_EARNINGS) < 0) {
        add(
          index,
          r,
          CHECK_CODES.INVALID_AMOUNT,
          `Box 2 (earnings) is a loss of ${String(r.BOX2_EARNINGS).trim()}. The IRS instructions ` +
            "allow a loss only in the final year of distributions from the account; in any other " +
            "year enter 0 in box 2 (and box 3 then equals box 1).",
          "BOX2_EARNINGS",
          "warning",
          "BOX2_LOSS"
        );
      }

      const box5 = [
        ["BOX5_PRIVATE_QTP", "private QTP"],
        ["BOX5_STATE_QTP", "state QTP"],
        ["BOX5_COVERDELL_ESA", "Coverdell ESA"],
      ].filter(([field]) => canonicalCheckbox(r[field]) === "X");
      if (box5.length === 0) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "Box 5 has no option ticked. Tick the one that says where the distribution comes " +
            "from: a private QTP, a state QTP or a Coverdell ESA (rule F1099Q002).",
          "BOX5_PRIVATE_QTP",
          "error",
          "F1099Q002"
        );
      } else if (box5.length > 1) {
        add(
          index,
          r,
          CHECK_CODES.CONFLICTING_BOXES,
          `Box 5 has more than one option ticked (${box5.map(([, name]) => name).join(", ")}). ` +
            "Box 5 takes one: the distribution comes from a private QTP, a state QTP or a " +
            "Coverdell ESA. Tick only the one that applies (rule F1099Q002).",
          box5[1][0],
          "error",
          "F1099Q002"
        );
      }

      // IRIS form rules F1099Q001 and F1099Q003 (TY2025 v2.1 and TY2026 v1.2 rules, both
      // "Report Error and Reject if Over Threshold"): box 1 must be a non-zero amount, and a box 3
      // that has a value must equal box 1 minus box 2, a blank box 2 counting as zero. A blank
      // box 3 passes the rule, but the instructions ask for it, so that is only a warning.
      const filled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== "";
      const gross = toAmount(r.BOX1_GROSS_DISTRIBUTION);
      if (gross === 0) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "Box 1 (gross distribution) is blank or zero. The IRS rejects a 1099-Q without a " +
            "gross distribution (rule F1099Q003).",
          "BOX1_GROSS_DISTRIBUTION",
          "error",
          "F1099Q003"
        );
      }
      if (filled(r.BOX3_BASIS)) {
        const earnings = toAmount(r.BOX2_EARNINGS);
        const basis = toAmount(r.BOX3_BASIS);
        if (Math.abs(gross - earnings - basis) > 0.005) {
          add(
            index,
            r,
            CHECK_CODES.INVALID_AMOUNT,
            `Box 3 (basis) is ${basis.toFixed(2)}, but box 1 minus box 2 is ` +
              `${(gross - earnings).toFixed(2)}. The IRS requires box 3 to equal box 1 minus box 2 ` +
              "(rule F1099Q001).",
            "BOX3_BASIS",
            "error",
            "F1099Q001"
          );
        }
      } else if (gross !== 0) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "Box 3 (basis) is blank. The IRS instructions ask for the basis, equal to box 1 minus " +
            "box 2.",
          "BOX3_BASIS",
          "warning",
          "BOX3_BLANK"
        );
      }

      // F1099Q008_001: a trustee-to-trustee transfer (4a) and a QTP-to-Roth IRA transfer (4b)
      // cannot both be ticked.
      if (
        canonicalCheckbox(r.BOX4A_TRUSTEE_TO_TRUSTEE_TRANSFER) === "X" &&
        canonicalCheckbox(r.BOX4B_QTP_TO_ROTH_IRA_TRANSFER) === "X"
      ) {
        add(
          index,
          r,
          CHECK_CODES.CONFLICTING_BOXES,
          "Box 4a (trustee-to-trustee transfer) and box 4b (QTP to Roth IRA transfer) are both " +
            "ticked. The IRS accepts one or neither (rule F1099Q008_001).",
          "BOX4B_QTP_TO_ROTH_IRA_TRANSFER",
          "error",
          "F1099Q008_001"
        );
      }

      // Two forms for one recipient need an account number each to tell them apart.
      const tin = String(r.RECIPIENT_ID_NUMBER ?? "").replace(/\D/g, "");
      if (tin && !filled(r.ACCOUNT_NUMBER) && (q1099Tins.get(tin) ?? 0) > 1) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "This recipient has more than one Form 1099-Q in the file and this one has no account " +
            "number. The IRS requires an account number when a recipient has more than one account.",
          "ACCOUNT_NUMBER",
          "warning",
          "DUPLICATE_RECIPIENT_NO_ACCOUNT"
        );
      }

      // A street line, US or foreign. The generator would otherwise file the placeholder
      // "UNKNOWN ADDRESS" (or "NA" abroad); the shared US checks below cover city and ZIP only.
      const street = [r.RECIPIENT_ADDRESS1, r.RECIPIENT_ADDRESS2, r.RECIPIENT_APT_NO]
        .map((v) => String(v ?? "").trim())
        .some(Boolean);
      if (!street) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "The recipient's street address is missing. The IRS return needs a street line.",
          "RECIPIENT_ADDRESS1"
        );
      }

      const code = String(r.DISTRIBUTION_CODE ?? "").trim();
      if (code !== "" && !/^[1-6]$/.test(code)) {
        add(
          index,
          r,
          CHECK_CODES.INVALID_CODE,
          `The distribution code is "${code}". The IRS accepts a single digit from 1 to 6, or blank.`,
          "DISTRIBUTION_CODE"
        );
      }
    }

    // ---- 3. tax year ----
    // The year being filed is the one the caller passed - the year selected in the app, or
    // named on the API request - and only that year decides whether it can be filed.
    if (!Number.isFinite(taxYear) || taxYear < oldestSchemaYear) {
      add(
        index,
        r,
        CHECK_CODES.UNSUPPORTED_TAX_YEAR,
        `Tax year ${taxYear} cannot be filed: this service files ` +
          `${SUPPORTED_SCHEMA_YEARS.slice().sort().join(" and ")}.`,
        map.taxYear.find((f) => r[f] !== undefined) ?? "TAX_YEAR"
      );
    } else {
      // A row whose own year column names another year is refused rather than filed under
      // the selected one (TS-8701): a 2025 file uploaded with 2026 selected is almost always
      // the wrong file or the wrong year, and once filed it can only be undone by the IRS.
      // A blank year column says nothing and passes.
      const yearField = map.taxYear.find((f) => r[f] !== undefined && r[f] !== null && String(r[f]).trim() !== "");
      const rowYear = yearField ? /^(\d{4})(?:\.0+)?$/.exec(String(r[yearField]).trim()) : null;
      if (yearField && rowYear && Number(rowYear[1]) !== taxYear) {
        add(
          index,
          r,
          CHECK_CODES.TAX_YEAR_MISMATCH,
          `${yearField} in the file (${rowYear[1]}) does not match the selected tax year (${taxYear}).`,
          yearField
        );
      }
    }

    // ---- 4. every column, by what it holds ----
    // TINs, ZIPs and postal codes, states, dates, phone numbers, email addresses, countries,
    // cities, address lines and names, on every column that carries one, against the rule the
    // IRS schema applies to it (fieldChecks.ts). This is what stops "gfdsagfeatgagffd" being
    // saved as a postal code and refused, a month later, by the IRS.
    //
    // A foreign recipient is decided by the country column (or the foreign indicator), NOT by
    // the state field, which for a foreign record usually holds a province. runSanitizers
    // rewrites the recipient USAddress into a valid ForeignAddress (sanitize/foreignAddress.ts),
    // so a foreign address files, and the US state and ZIP rules do not apply to it; the
    // postal code rule does.
    const foreignCountry = pick(r, map.foreignCountry);
    const foreignInd = String(r?.FOREIGN_COUNTRY_INDICATOR ?? "").trim();
    const isForeign = foreignCountry !== "" || (foreignInd !== "" && foreignInd !== "0");
    for (const issue of checkFieldValues(r, rules, {
      recipientForeign: isForeign,
      recipientZipField: map.zip[0],
    })) {
      add(index, r, issue.code, issue.message, issue.field);
    }

    // ---- 5. the recipient's address is whole ----
    // What can still fail on a foreign address is the country itself: the IRS accepts its own
    // two-letter code list, which is not ISO 3166 (Spain is SP, the Bahamas is BF). A country
    // the schema does not know is the one thing worth stopping for, and it names the country
    // the filer wrote.
    if (isForeign) {
      if (!foreignCountry) {
        add(
          index,
          r,
          CHECK_CODES.MISSING_FIELD,
          "This row is marked as a foreign address but names no country. " +
            "The IRS return needs the country.",
          map.foreignCountry[0] ?? "FOREIGN_COUNTRY"
        );
      } else if (
        !irsCountryCode(foreignCountry) &&
        // Step 4 has already said so when the country column is one it checks; once is enough.
        !issues.some((i) => i.index === index && i.code === CHECK_CODES.UNKNOWN_COUNTRY)
      ) {
        add(
          index,
          r,
          CHECK_CODES.UNKNOWN_COUNTRY,
          `The IRS does not recognise "${foreignCountry}" as a country. ` +
            "Use the country's full name as the IRS spells it, or its two-letter IRS code.",
          map.foreignCountry[0] ?? "FOREIGN_COUNTRY"
        );
      }
      return;
    }

    // US recipient: state, city and ZIP are all required. A missing state makes the generator
    // emit the invalid placeholder "XX"; the IRS USAddress requires CityNm and nothing can
    // derive one; the send-side sanitizer refuses a record without any of them, so the
    // customer hears it here, by record, rather than as a failed e-file an hour later.
    if (!pick(r, map.state)) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        "The recipient's state is missing. A US address must carry a two-letter state code.",
        pickField(r, map.state)
      );
    }
    if (!pick(r, map.city)) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        "The recipient's city is missing. A US address must carry a city.",
        map.city[0]
      );
    }
    if (!pick(r, map.zip)) {
      add(
        index,
        r,
        CHECK_CODES.MISSING_FIELD,
        `The recipient's ZIP code is missing. A US address must carry one, in the column ${map.zip[0]}.`,
        map.zip[0]
      );
    }
  });

  return {
    ok: !issues.some((i) => i.severity === "error"),
    issues,
    fileable: list.length - blockedRows.size,
    blocked: blockedRows.size,
  };
}
